import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const prefixTuningComponent: ComponentDef = {
  id: 'prefix-tuning',
  name: 'Prefix Tuning',
  description: 'Prepend learnable "virtual token" key/value vectors to every attention layer. The backbone is frozen; only the prefix parameters are trained.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'prefix_len', label: 'Prefix length', type: 'number', default: 10, min: 1, max: 128, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'prefix-tuning',
      options: [
        { label: 'Prefix Tuning (per-layer KV prefix)', value: 'prefix-tuning' },
        { label: 'Prompt Tuning (input embeddings only)', value: 'prompt-tuning' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const prefix_len = Number(params.prefix_len);
    const variant = String(params.variant || 'prefix-tuning');

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'prompt-tuning') {
      // Prompt Tuning: learned soft tokens only at the input embedding layer
      // Much simpler: just prepend a small trainable embedding to the input
      g.input('task_id', [1]);
      g.embedding('soft_prompt', 'task_id', [prefix_len, d_model]);
      g.concat('prompted_x', ['soft_prompt', 'x'], [seq_len + prefix_len, d_model]);

      // Frozen backbone: one linear stands in for the full transformer
      g.linear('frozen_backbone', 'prompted_x', [d_model, d_model]);
      // Slice away prefix positions from output
      g.reshape('out_tokens', 'frozen_backbone', [seq_len, d_model]);
      g.output('out', 'out_tokens');

    } else {
      // Prefix Tuning: learnable K and V prepended in every attention layer
      // Shown for one representative attention layer

      // Trainable prefix parameters (reparameterised through a small MLP for stability)
      g.input('prefix_params', [prefix_len, d_model]);
      g.linear('prefix_mlp1', 'prefix_params', [d_model, d_model]);
      g.activation('prefix_act', 'prefix_mlp1', 'Tanh');
      g.linear('prefix_kv', 'prefix_act', [d_model, d_model * 2]);  // produces K and V

      // Split into prefix K and prefix V
      g.reshape('P_K', 'prefix_kv', [prefix_len, d_model]);
      g.reshape('P_V', 'prefix_kv', [prefix_len, d_model]);

      // Frozen backbone Q projection on real input
      g.linear('Q', 'x', [d_model, d_model]);
      // Frozen K and V for real tokens
      g.linear('K_real', 'x', [d_model, d_model]);
      g.linear('V_real', 'x', [d_model, d_model]);

      // Concatenate prefix K/V with real K/V
      g.concat('K_full', ['P_K', 'K_real'], [prefix_len + seq_len, d_model]);
      g.concat('V_full', ['P_V', 'V_real'], [prefix_len + seq_len, d_model]);

      // Standard attention with extended K/V
      g.transpose('K_T', 'K_full', [d_model, prefix_len + seq_len], 'K^T');
      g.matmul('QK', 'Q', 'K_T', [seq_len, prefix_len + seq_len]);
      g.scale('QK_scaled', 'QK', '1/√d_k');
      g.softmax('attn_w', 'QK_scaled', -1);
      g.matmul('attn_out', 'attn_w', 'V_full', [seq_len, d_model]);
      g.linear('wo', 'attn_out', [d_model, d_model]);
      g.output('out', 'wo');
    }

    return g.build();
  }
};
